/**
 * `/mail` Overview — Mail dashboard landing page (OCP-128).
 *
 * Three states, mirroring the API response:
 *   - OMS unconfigured → "Mail bridge offline" amber notice
 *   - Customer not in OMS yet → "Connect or mint a key" empty state
 *     with two clear CTAs (mint API key, OAuth-connect an app)
 *   - Happy path → quota widget + recent-sends widget
 *
 * Quota widget uses a single horizontal bar (filled, colored by %).
 * Recent-sends widget shows top-5 with status pill + relative time.
 */
import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { MailQuotaBanner } from '../components/MailQuotaBanner';
import { MailDeliverabilityBanner, MailDeliverabilityCard, type Deliverability } from '../components/MailDeliverabilityCard';
import { ApiError, api } from '../lib/api';
import { fmtNumber } from '../lib/format-money';

interface OverviewQuota {
  used: number;
  total: number | null;
  period_end: string | null;
}

interface OverviewSend {
  id: string;
  to: string;
  subject: string;
  status: string;
  sent_at: string;
}

interface OverviewResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  quota: OverviewQuota | null;
  recent_sends: OverviewSend[];
  /** OCP-621 */
  deliverability?: Deliverability | null;
}

export function MailOverviewPage() {
  const { t } = useTranslation('mail-overview');
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setError(null);
    try {
      const r = await api<OverviewResponse>('/v1/portal/mail/overview', { signal });
      setData(r);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError && err.status === 502) {
        setError(t('error.unreachable'));
        return;
      }
      if (err instanceof ApiError) {
        setError(t('error.http', { status: err.status }));
        return;
      }
      setError(t('error.generic'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-800 dark:text-red-300"
      >
        {error}
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-gray-500">{t('loading')}</p>;
  }
  if (!data.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.text')}</p>
      </div>
    );
  }
  if (!data.customer_exists) {
    return <NoMailAccountYet />;
  }

  return (
    <>
      <MailQuotaBanner />
      <MailDeliverabilityBanner deliverability={data.deliverability ?? null} />
      <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
        <div className="space-y-6">
          <QuotaCard quota={data.quota} />
          <MailDeliverabilityCard deliverability={data.deliverability ?? null} />
        </div>
        <RecentSendsCard sends={data.recent_sends} />
      </div>
    </>
  );
}

function NoMailAccountYet() {
  const { t } = useTranslation('mail-overview');
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t('start.title')}
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {t('start.lead')}
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Link
          to="/mail/api-keys"
          className="block rounded-lg border border-brand-200 bg-brand-50/40 p-5 transition hover:border-brand-400 hover:bg-brand-50"
        >
          <div className="text-sm font-semibold text-brand-700">{t('start.apiKey')}</div>
          <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
            {t('start.apiKeyBefore')}
            <code className="mx-1 rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-xs">@orboto/mail</code>
            {t('start.apiKeyAfter')}
          </p>
        </Link>
        <Link
          to="/mail/connections"
          className="block rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 transition hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('start.connect')}</div>
          <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
            {t('start.connectText')}
          </p>
        </Link>
      </div>
      <p className="mt-5 text-xs text-gray-500">
        {t('start.domainQuestion')}{' '}
        <Link to="/mail/domains" className="text-brand-500 hover:underline">
          {t('start.domainLink')}
        </Link>
      </p>
    </div>
  );
}

function QuotaCard({ quota }: { quota: OverviewQuota | null }) {
  const { t } = useTranslation('mail-overview');
  if (!quota) {
    return (
      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('quota.title')}</h2>
        <p className="mt-2 text-sm text-gray-500">{t('quota.noUsage')}</p>
      </section>
    );
  }
  const total = quota.total ?? 0;
  const pct = total > 0 ? Math.min(100, (quota.used / total) * 100) : 0;
  const tone =
    pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand-500';
  const periodLabel = quota.period_end
    ? t('quota.resets', { when: formatRelative(t, quota.period_end, { future: true }) })
    : t('quota.rolling');

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('quota.title')}</h2>
        <span className="text-xs text-gray-500">{periodLabel}</span>
      </header>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {fmtNumber(quota.used)}
        </span>
        {total > 0 && (
          <span className="text-sm text-gray-500">
            {t('quota.ofSends', { total: fmtNumber(total) })}
          </span>
        )}
      </div>
      {total > 0 ? (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={quota.used}
          className="mt-4 h-2 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800"
        >
          <div
            className={`h-full transition-all ${tone}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <p className="mt-4 text-xs text-gray-500">
          {t('quota.noPlanBefore')}{' '}
          <Link to="/products" className="text-brand-500 hover:underline">
            {t('quota.noPlanLink')}
          </Link>{' '}
          {t('quota.noPlanAfter')}
        </p>
      )}
      <div className="mt-4 flex justify-between text-xs">
        <Link to="/mail/sends" className="text-brand-500 hover:underline">
          {t('quota.viewAll')}
        </Link>
        <Link to="/mail/settings" className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-100">
          {t('quota.planSettings')}
        </Link>
      </div>
    </section>
  );
}

function RecentSendsCard({ sends }: { sends: OverviewSend[] }) {
  const { t } = useTranslation('mail-overview');
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <header className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('recent.title')}</h2>
        <Link to="/mail/sends" className="text-xs text-brand-500 hover:underline">
          {t('recent.seeAll')}
        </Link>
      </header>
      {sends.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          {t('recent.empty')}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {sends.map((s) => (
            <li key={s.id} className="flex items-start gap-3 px-5 py-3">
              <StatusPill status={s.status} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {s.subject || t('recent.noSubject')}
                </div>
                <div className="truncate text-xs text-gray-500">
                  {t('recent.to', { to: s.to })}
                </div>
              </div>
              <div className="shrink-0 text-xs tabular-nums text-gray-400">
                {formatRelative(t, s.sent_at, { future: false })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation('mail-overview');
  const tone =
    status === 'delivered'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50'
      : status === 'bounced' || status === 'rejected' || status === 'failed'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50'
        : status === 'complained'
          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span
      className={`mt-0.5 inline-flex shrink-0 rounded border px-1.5 py-0.5 text-xs ${tone}`}
    >
      {t(`status.${status}`, { defaultValue: status })}
    </span>
  );
}

/**
 * Tiny human-relative-time helper. Resend/UseSend style: "2m ago",
 * "3h ago", "in 12d". Avoids pulling date-fns just for one widget.
 */
function formatRelative(t: TFunction<'mail-overview'>, iso: string, opts: { future: boolean }): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffMs = target - Date.now();
  const abs = Math.abs(diffMs);
  const future = diffMs > 0;
  const m = 60 * 1000;
  const h = 60 * m;
  const d = 24 * h;
  if (abs < m) return t('relative.justNow');
  const v = abs < h ? t('relative.m', { n: Math.floor(abs / m) }) : abs < d ? t('relative.h', { n: Math.floor(abs / h) }) : t('relative.d', { n: Math.floor(abs / d) });
  return opts.future && future ? t('relative.in', { v }) : t('relative.ago', { v });
}
